import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import Rating from '@mui/material/Rating';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import { alpha, useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import GridContainer from '../common/GridContainer';
import GridItem from '../common/GridItem';

// Shared testimonials data
import { testimonials } from '../../data/testimonials';

// Helper function to get initials from name
const getInitials = (name: string): string => {
  const parts = name.trim().split(' ');
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0][0].toUpperCase();
  // First letter of first name and first letter of last name
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const TestimonialSection: React.FC = () => {
  const theme = useTheme();
  const [activeIndex, setActiveIndex] = useState(0);

  // Section title styling consistent with HomePage
  const sectionTitleStyles = {
    textAlign: 'center',
    mb: { xs: 6, md: 8 },
    mx: 'auto',
    maxWidth: 900
  };

  // Section subtitle styling
  const sectionSubtitleStyles = {
    color: 'secondary.main',
    fontWeight: 600,
    mb: 1,
    letterSpacing: 1,
    textTransform: 'uppercase',
    textAlign: 'center'
  };

  // Section heading styling
  const sectionHeadingStyles = {
    fontFamily: 'Playfair Display, serif',
    fontWeight: 600,
    mb: 3,
    fontSize: { xs: '2.25rem', md: '3rem' },
    textAlign: 'center'
  };

  // Section description styling
  const sectionDescriptionStyles = {
    color: 'text.secondary',
    maxWidth: 800,
    mx: 'auto',
    fontSize: { xs: '1rem', md: '1.1rem' },
    lineHeight: 1.7,
    textAlign: 'center'
  };

  const handlePrev = () => {
    setActiveIndex((prevIndex) => 
      prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1
    );
  };

  const handleNext = () => {
    setActiveIndex((prevIndex) => 
      prevIndex === testimonials.length - 1 ? 0 : prevIndex + 1
    );
  };

  return (
    <Box
      sx={{
        py: { xs: 6, md: 10 },
        background: 'linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Decorative elements */}
      <Box
        sx={{
          position: 'absolute',
          top: -100,
          left: -100,
          width: 300,
          height: 300,
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
          opacity: 0.05,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: -80,
          right: -80,
          width: 250,
          height: 250,
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${theme.palette.secondary.light} 0%, ${theme.palette.secondary.main} 100%)`,
          opacity: 0.05,
        }}
      />

      <Container maxWidth="lg">
        <Box sx={sectionTitleStyles}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <Typography
              variant="h6"
              component="p"
              sx={sectionSubtitleStyles}
            >
              CLIENT TESTIMONIALS
            </Typography>
            <Typography
              variant="h3"
              component="h2"
              sx={sectionHeadingStyles}
            >
              What Our Clients Say
            </Typography>
            <Typography
              variant="body1"
              sx={sectionDescriptionStyles}
            >
              Don't just take our word for it. Hear what our clients have to say about 
              their experience with our premium transportation services - from corporate travel to personal bookings.
            </Typography>
          </motion.div>
        </Box>

        <Box 
          sx={{ 
            position: 'relative',
            px: { xs: 0, md: 6 },
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 5 },
              borderRadius: 2,
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.05)',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >

            <GridContainer spacing={4} alignItems="center">
              <GridItem xs={12} md={4}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: { xs: 'center', md: 'flex-start' },
                    mb: { xs: 4, md: 0 },
                  }}
                >
                  <motion.div
                    key={testimonials[activeIndex].id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5 }}
                  >
                    <Avatar
                      sx={{
                        width: 160,
                        height: 160,
                        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
                        border: '5px solid white',
                        mb: 3,
                        bgcolor: 'primary.main',
                        color: 'white',
                        fontSize: '3.5rem',
                        fontWeight: 600,
                      }}
                    >
                      {getInitials(testimonials[activeIndex].name)}
                    </Avatar>
                  </motion.div>

                  <motion.div
                    key={`details-${testimonials[activeIndex].id}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                  >
                    <Typography
                      variant="h5"
                      component="h3"
                      sx={{ 
                        fontWeight: 600,
                        textAlign: { xs: 'center', md: 'left' },
                      }}
                    >
                      {testimonials[activeIndex].name}
                    </Typography>
                    {testimonials[activeIndex].isCorporate && testimonials[activeIndex].position && testimonials[activeIndex].company && (
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ 
                        mb: 1,
                        textAlign: { xs: 'center', md: 'left' },
                      }}
                    >
                      {testimonials[activeIndex].position}, {testimonials[activeIndex].company}
                    </Typography>
                    )}
                    <Rating
                      value={testimonials[activeIndex].rating}
                      readOnly
                      sx={{ mb: 1 }}
                    />
                  </motion.div>
                </Box>
              </GridItem>

              <GridItem xs={12} md={8}>
                <motion.div
                  key={`quote-${testimonials[activeIndex].id}`}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <Box
                    sx={{
                      position: 'relative',
                      pl: { xs: 2, md: 3 },
                    }}
                  >
                    <FormatQuoteIcon
                      sx={{
                        position: 'absolute',
                        top: -10,
                        left: 0,
                        fontSize: '4rem',
                        color: alpha(theme.palette.primary.main, 0.08),
                        transform: 'scaleX(-1)',
                        zIndex: 0,
                      }}
                    />
                    <Typography
                      variant="h6"
                      component="blockquote"
                      sx={{ 
                        fontStyle: 'italic',
                        fontWeight: 400,
                        lineHeight: 1.8,
                        mb: 3,
                        position: 'relative',
                        zIndex: 1,
                        fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
                        color: 'text.primary',
                        px: { xs: 1, md: 2 },
                        pt: 1,
                      }}
                    >
                      {testimonials[activeIndex].testimonial}
                    </Typography>
                  </Box>
                </motion.div>
              </GridItem>
            </GridContainer>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: 4,
              }}
            >
              <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                <IconButton
                  onClick={handlePrev}
                  sx={{
                    mr: 2,
                    bgcolor: 'background.paper',
                    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
                    '&:hover': {
                      bgcolor: 'primary.light',
                    },
                  }}
                >
                  <NavigateBeforeIcon />
                </IconButton>
              </motion.div>
              <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                <IconButton
                  onClick={handleNext}
                  sx={{
                    bgcolor: 'background.paper',
                    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
                    '&:hover': {
                      bgcolor: 'primary.light',
                    },
                  }}
                >
                  <NavigateNextIcon />
                </IconButton>
              </motion.div>
            </Box>
          </Paper>
          
          <Box
            sx={{
              mt: 5,
              textAlign: 'center',
            }}
          >
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Button
                component={RouterLink}
                to="/testimonials"
                variant="outlined"
                color="primary"
                size="large"
                endIcon={<ArrowForwardIcon />}
                sx={{
                  px: 4,
                  py: 1.2,
                  borderWidth: 2,
                  '&:hover': {
                    borderWidth: 2,
                  },
                }}
              >
                View All Testimonials
              </Button>
            </motion.div>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default TestimonialSection; 